import { useCallback, useEffect, useState } from 'react';
import { ApiError, apiGet, apiPatch, apiPost } from '../api/client.js';
import { Layout } from '../components/Layout.js';

/**
 * API Keys admin page (kb_admin only).
 *
 * Lists every key with last-used + monthly call count. Lets the operator
 * create / rotate / revoke. The plaintext key value is shown EXACTLY ONCE
 * in a reveal modal at create + rotate time. Closing the modal drops the
 * plaintext from memory; there is no recovery path.
 */

const AG_UI_ENDPOINT = 'https://kb.1nebula.com/api/v1/agent/ag-ui/stream';
const AG_UI_DOCS_URL = 'https://kb.1nebula.com/api/v1/agent/ag-ui/docs';

/**
 * Ready-to-send onboarding note for an integrating party. Admins were pasting
 * ad-hoc instructions (or the key itself) into chat; this gives them one
 * correct, key-free message to forward. Deliberately points at the live
 * /docs contract as the source of truth rather than restating it — that
 * endpoint self-updates, this string can't.
 */
const INTEGRATOR_NOTE = `Connecting to the OneNebula Knowledgebase
=========================================

The KB answers natural-language questions from a curated corpus (product docs,
policy, runbooks, Confluence, Jira, code). Answers are grounded with citations,
and the agent refuses rather than guesses when it has no source.

Two ways in — pick what matches what you're building:

  • Your own app / script / AI agent  ->  AG-UI endpoint (one HTTPS call, streamed cited answer)
  • Your IDE (Claude Desktop, Cursor, Copilot Chat)  ->  MCP server (runs locally, adds KB tools)

("A2A" is our internal backend<->agent protocol, secured by Cloud Run IAM. It
isn't exposed — if someone asks for A2A access, they want AG-UI.)

1. GET YOUR OWN KEY
   Ask a OneNebula kb_admin for a key issued to YOU (or your service).
   Please don't reuse a colleague's: each key carries its own role (which
   decides what content it can see), its own rate limit, and its own usage
   trail. Shared keys break revocation and the audit trail.

   The key is shown once and is unrecoverable — store it in your secret
   store / IDE config immediately. Never in chat, tickets, or git.

2a. AG-UI (apps, scripts, agents)
   POST ${AG_UI_ENDPOINT}
   Authorization: Bearer <YOUR_KEY>

   Smoke test:
   curl -N -X POST ${AG_UI_ENDPOINT} \\
     -H "Authorization: Bearer <YOUR_KEY>" \\
     -H "Content-Type: application/json" \\
     -d '{"messages":[{"role":"user","content":"What is OneView?"}]}'

   You get a Server-Sent Events stream: RUN_STARTED, TEXT_MESSAGE_CONTENT
   deltas (stream these into your UI), a STATE_DELTA carrying the CITATIONS
   (please surface them — they're the point), then RUN_FINISHED.

   FULL CONTRACT (no auth needed, always current — this is the source of truth;
   your agent can fetch it and self-configure):
   ${AG_UI_DOCS_URL}

   Errors: 401 = key invalid/revoked (re-copy or ask for a rotate).
           429 = over your rate limit (default 60/min; ask us to raise it).
           503 = agent briefly unavailable, retry.

2b. MCP (IDE use)
   Adds three tools to your IDE: search_kb, get_document, cite_chunks.
   Runs locally as a child process of your IDE — nothing to deploy.
   Setup + IDE config snippets: knowledgebase-mcp/README.md in the repo.
   Verify by asking: "Use the OneNebula KB to look up the OneView design system."

3. IF YOUR KEY LEAKS
   Tell us immediately — we revoke it on the spot (no grace period) and issue
   a new one. Rotation takes effect within 60 seconds.

Questions: ask your OneNebula kb_admin contact.`;

function IntegratorNote(): JSX.Element {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [open, setOpen] = useState(false);
  return (
    <section className="mt-4 rounded-xl border border-nebula-border-soft bg-nebula-tint/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-sm text-nebula-fg-1">
            Sharing access with an integrating party?
          </h2>
          <p className="mt-0.5 text-xs text-nebula-fg-3">
            Send them this note — it explains both surfaces, how to smoke-test, and the key rules.
            It contains no key: issue theirs below and deliver it separately.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={(): void => {
              // Only claim success if the write actually resolved — a denied
              // clipboard (permissions, insecure context) must not show
              // "Copied" and leave the admin pasting nothing.
              navigator.clipboard
                .writeText(INTEGRATOR_NOTE)
                .then(() => setCopyState('copied'))
                .catch(() => {
                  setCopyState('failed');
                  setOpen(true); // fall back: reveal the text to copy by hand
                })
                .finally(() => setTimeout(() => setCopyState('idle'), 2500));
            }}
            className="rounded-md bg-nebula-primary px-3 py-1.5 text-xs font-medium text-white"
          >
            {copyState === 'copied'
              ? 'Copied ✓'
              : copyState === 'failed'
                ? 'Copy blocked — select below'
                : 'Copy note'}
          </button>
          <button
            type="button"
            onClick={(): void => setOpen((s) => !s)}
            className="rounded-md border border-nebula-border px-3 py-1.5 text-xs text-nebula-fg-2 hover:bg-nebula-surface"
          >
            {open ? 'Hide' : 'Preview'}
          </button>
        </div>
      </div>
      {open && (
        <pre className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap rounded border border-nebula-border bg-nebula-surface p-3 font-mono text-[11px] leading-relaxed text-nebula-fg-2">
          {INTEGRATOR_NOTE}
        </pre>
      )}
    </section>
  );
}

/** House rules for issuing keys — the bits admins get wrong. */
function IntegrationGuide(): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={(): void => setOpen((s) => !s)}
        className="text-xs text-nebula-secondary hover:underline"
      >
        {open ? 'Hide issuing guidance' : 'How to issue a key properly →'}
      </button>
      {open && (
        <div className="mt-2 rounded-lg border border-nebula-border-soft bg-nebula-surface p-4 text-sm text-nebula-fg-2">
          <ul className="list-disc space-y-1.5 pl-5">
            <li>
              <span className="font-medium text-nebula-fg-1">One key per consumer.</span> Label it
              for a person (<code className="font-mono text-xs">mcp-andre</code>) or a service (
              <code className="font-mono text-xs">bizops-prod</code>) — never a team. Shared keys
              break revocation, rate limits and the usage trail.
            </li>
            <li>
              <span className="font-medium text-nebula-fg-1">The role is the visibility gate.</span>{' '}
              A key sees what its role sees. Use <code className="font-mono text-xs">kb_user</code>{' '}
              unless the holder already has higher access via Entra — issuing{' '}
              <code className="font-mono text-xs">confidential_user</code> to someone who lacks it
              hands them content they can't otherwise see.
            </li>
            <li>
              <span className="font-medium text-nebula-fg-1">Deliver the key out of band.</span>{' '}
              Secret store or password manager — not chat, tickets, or email. Send the note above
              separately.
            </li>
            <li>
              <span className="font-medium text-nebula-fg-1">Rotate on any doubt.</span> Rotation is
              instant (old value dies within 60s) and costs the holder one config update. Revoke has
              no grace period — use it the moment a key may have leaked.
            </li>
            <li>
              Watch the <span className="font-medium text-nebula-fg-1">Last used</span> and monthly
              call columns below: a key that has never been used weeks after issue is probably lost
              — revoke it rather than leaving it live.
            </li>
          </ul>
          <p className="mt-3 text-xs text-nebula-fg-3">
            Full runbook (issuance, rotation, alert response, incident triage):{' '}
            <code className="font-mono text-xs">docs/runbooks/external-integrations.md</code>. Live
            integration contract:{' '}
            <a
              href={AG_UI_DOCS_URL}
              target="_blank"
              rel="noreferrer"
              className="text-nebula-secondary hover:underline"
            >
              /api/v1/agent/ag-ui/docs
            </a>
            .
          </p>
        </div>
      )}
    </div>
  );
}

interface ApiKeyPublic {
  id: string;
  label: string;
  prefix: string;
  keyPreview: string;
  role: string;
  tenantId: string;
  rpmLimit: number;
  status: 'active' | 'revoked';
  createdAt: string;
  createdBy: string;
  revokedAt?: string;
  revokedBy?: string;
  lastUsedAt?: string;
}

interface ApiKeyCreateResponse {
  key: ApiKeyPublic;
  plaintext: string;
}

interface UsageRow {
  apiKeyId: string;
  month: string;
  callCount: number;
  lastCallAt?: string;
}

const ROLES: Array<{ value: string; label: string }> = [
  { value: 'kb_user', label: 'Employee (kb_user)' },
  { value: 'exco_user', label: 'Exco (exco_user)' },
  { value: 'sensitive_user', label: 'Sensitive (sensitive_user)' },
  { value: 'confidential_user', label: 'Confidential (confidential_user)' },
  { value: 'kb_admin', label: 'Admin (kb_admin)' },
];

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message ?? 'Request failed.';
  return err instanceof Error ? err.message : 'Request failed.';
}

export function AdminApiKeysPage(): JSX.Element {
  const [keys, setKeys] = useState<ApiKeyPublic[]>([]);
  const [usage, setUsage] = useState<Record<string, UsageRow>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [draftLabel, setDraftLabel] = useState('');
  const [draftRole, setDraftRole] = useState('kb_user');
  const [draftRpm, setDraftRpm] = useState(60);
  const [creating, setCreating] = useState(false);

  const [reveal, setReveal] = useState<{
    keyId: string;
    plaintext: string;
    action: 'created' | 'rotated';
  } | null>(null);
  const [keyCopyState, setKeyCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiGet<{ keys: ApiKeyPublic[] }>('/api/v1/admin/api-keys');
      setKeys(res.keys);
      // Usage rollup. The view returns empty in non-BQ envs; failures are
      // non-fatal — the table still renders without usage chips.
      try {
        const u = await apiGet<{ usage: UsageRow[] }>('/api/v1/admin/api-keys/usage');
        const map: Record<string, UsageRow> = {};
        for (const row of u.usage) map[row.apiKeyId] = row;
        setUsage(map);
      } catch {
        setUsage({});
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onCreate = async (): Promise<void> => {
    const label = draftLabel.trim();
    if (!label) return;
    setCreating(true);
    setError(null);
    try {
      const res = await apiPost<ApiKeyCreateResponse>('/api/v1/admin/api-keys', {
        label,
        role: draftRole,
        rpmLimit: draftRpm,
      });
      setReveal({ keyId: res.key.id, plaintext: res.plaintext, action: 'created' });
      setDraftLabel('');
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const onRotate = async (k: ApiKeyPublic): Promise<void> => {
    if (!confirm(`Rotate "${k.label}"? The previous key stops working immediately.`)) return;
    setBusyId(k.id);
    try {
      const res = await apiPatch<ApiKeyCreateResponse>(`/api/v1/admin/api-keys/${k.id}`, {
        action: 'rotate',
      });
      setReveal({ keyId: res.key.id, plaintext: res.plaintext, action: 'rotated' });
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const onRevoke = async (k: ApiKeyPublic): Promise<void> => {
    if (!confirm(`Revoke "${k.label}"? This cannot be undone.`)) return;
    setBusyId(k.id);
    try {
      await apiPatch(`/api/v1/admin/api-keys/${k.id}`, { action: 'revoke' });
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-5xl px-6 py-8">
        <h1 className="font-display text-2xl text-nebula-fg-1">API Keys</h1>
        <p className="mt-1 text-sm text-nebula-fg-3">
          Issue, rotate and revoke API keys for external integrations (AG-UI streaming, MCP). Keys
          are shown exactly once at create or rotate time — copy and store immediately.
        </p>

        <IntegratorNote />
        <IntegrationGuide />

        {error && (
          <div className="mt-4 rounded border border-nebula-border bg-nebula-warning-bg p-3 text-sm text-nebula-warning">
            {error}
          </div>
        )}

        {/* Create form */}
        <section className="mt-6 rounded-xl border border-nebula-border-soft bg-nebula-surface p-5">
          <h2 className="font-display text-lg text-nebula-fg-1">New key</h2>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-4">
            <input
              type="text"
              value={draftLabel}
              onChange={(e): void => setDraftLabel(e.target.value)}
              placeholder="Label e.g. marketing-prod"
              className="rounded border border-nebula-border px-3 py-2 text-sm sm:col-span-2"
              maxLength={120}
            />
            <select
              value={draftRole}
              onChange={(e): void => setDraftRole(e.target.value)}
              className="rounded border border-nebula-border px-3 py-2 text-sm"
            >
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <input
              type="number"
              value={draftRpm}
              onChange={(e): void => setDraftRpm(Math.max(1, parseInt(e.target.value || '60', 10)))}
              min={1}
              max={10000}
              className="rounded border border-nebula-border px-3 py-2 text-sm"
            />
          </div>
          <button
            type="button"
            onClick={(): void => {
              void onCreate();
            }}
            disabled={creating || draftLabel.trim().length === 0}
            className="mt-3 rounded-md bg-nebula-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {creating ? 'Creating…' : 'Create key'}
          </button>
        </section>

        {/* List */}
        <section className="mt-8">
          <h2 className="font-display text-lg text-nebula-fg-1">Issued keys</h2>
          {loading ? (
            <div className="mt-4 text-sm text-nebula-fg-3">Loading…</div>
          ) : keys.length === 0 ? (
            <div className="mt-4 text-sm text-nebula-fg-3">No keys issued yet.</div>
          ) : (
            <table className="mt-4 w-full text-sm">
              <thead>
                <tr className="text-left text-nebula-fg-3">
                  <th className="py-2">Label</th>
                  <th className="py-2">Preview</th>
                  <th className="py-2">Role</th>
                  <th className="py-2">RPM</th>
                  <th className="py-2">Last used</th>
                  <th className="py-2">This month</th>
                  <th className="py-2">Status</th>
                  <th className="py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => {
                  const u = usage[k.id];
                  return (
                    <tr key={k.id} className="border-t border-nebula-border-soft">
                      <td className="py-2 font-medium text-nebula-fg-1">{k.label}</td>
                      <td className="py-2 font-mono text-nebula-fg-2">
                        {k.prefix}
                        {k.keyPreview}
                      </td>
                      <td className="py-2 text-nebula-fg-2">{k.role}</td>
                      <td className="py-2 text-nebula-fg-2">{k.rpmLimit}</td>
                      <td className="py-2 text-nebula-fg-3">
                        {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : '—'}
                      </td>
                      <td className="py-2 text-nebula-fg-3">{u ? u.callCount : '—'}</td>
                      <td className="py-2">
                        {k.status === 'active' ? (
                          <span className="inline-flex rounded-full bg-nebula-tint px-2 py-0.5 text-xs text-nebula-secondary">
                            active
                          </span>
                        ) : (
                          <span className="inline-flex rounded-full bg-nebula-gray-5 px-2 py-0.5 text-xs text-nebula-fg-2">
                            revoked
                          </span>
                        )}
                      </td>
                      <td className="py-2 text-right">
                        {k.status === 'active' && (
                          <>
                            <button
                              type="button"
                              onClick={(): void => {
                                void onRotate(k);
                              }}
                              disabled={busyId === k.id}
                              className="rounded border border-nebula-border px-2 py-1 text-xs text-nebula-fg-2 hover:bg-nebula-tint disabled:opacity-50"
                            >
                              Rotate
                            </button>
                            <button
                              type="button"
                              onClick={(): void => {
                                void onRevoke(k);
                              }}
                              disabled={busyId === k.id}
                              className="ml-2 rounded border border-nebula-border px-2 py-1 text-xs text-nebula-warning hover:bg-nebula-warning-bg disabled:opacity-50"
                            >
                              Revoke
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      </div>

      {/* Reveal modal — one-shot plaintext. Closes drop the value. */}
      {reveal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-nebula-fg-1/40 px-4">
          <div className="w-full max-w-lg rounded-xl bg-nebula-surface p-6 shadow-card">
            <h3 className="font-display text-lg text-nebula-fg-1">
              Key {reveal.action === 'rotated' ? 'rotated' : 'created'} — copy now
            </h3>
            <p className="mt-2 text-sm text-nebula-fg-3">
              This is the only time you will see this key. Store it in a secrets manager now.
              Closing this dialog erases it from memory.
            </p>
            <pre className="mt-4 whitespace-pre-wrap break-all rounded border border-nebula-border bg-nebula-app p-3 font-mono text-sm text-nebula-fg-1">
              {reveal.plaintext}
            </pre>
            {/* Handing over is the riskiest moment — say how, right here, rather
                than hoping the admin read the runbook. */}
            <p className="mt-3 rounded border border-nebula-border-soft bg-nebula-tint/50 px-3 py-2 text-xs text-nebula-fg-2">
              <span className="font-medium text-nebula-fg-1">Handing this to someone?</span> Send
              the key through a secret store or password manager — never chat, tickets or email.
              Forward the &ldquo;Sharing access&rdquo; note from this page separately: it explains
              the endpoints and smoke test without containing the key.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={(): void => {
                  // A silently-failed copy here is the worst case: the admin
                  // closes the modal believing they have the key, and it is
                  // unrecoverable. Only confirm on a resolved write.
                  navigator.clipboard
                    .writeText(reveal.plaintext)
                    .then(() => setKeyCopyState('copied'))
                    .catch(() => setKeyCopyState('failed'))
                    .finally(() => setTimeout(() => setKeyCopyState('idle'), 2500));
                }}
                className="rounded border border-nebula-border px-3 py-1.5 text-sm text-nebula-fg-2 hover:bg-nebula-tint"
              >
                {keyCopyState === 'copied'
                  ? 'Copied ✓'
                  : keyCopyState === 'failed'
                    ? 'Copy blocked — select above'
                    : 'Copy'}
              </button>
              <button
                type="button"
                onClick={(): void => setReveal(null)}
                className="rounded bg-nebula-primary px-3 py-1.5 text-sm text-white"
              >
                I have saved it
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
